<template>
  <div>
    <!-- Trigger button to open modal -->
    <div
      class="is-rounded"
      :class="{
        'opacity-50': disabled,
        'is-clickable': !disabled,
      }"
      @click="openMapModal"
      :aria-disabled="disabled"
      :tabindex="disabled ? -1 : 0"
      style="margin-top: 10px;margin-bottom: 20px;border: 1px solid rgb(219 219 219);border-radius: 10px;"
    >
      <img
        :src="
          `https://maps.googleapis.com/maps/api/staticmap?center=${encodeURIComponent(
            searchLocation.name || ''
          )}&zoom=10&size=300x150&maptype=roadmap
&markers=color:red%7C${searchLocation.latitude},${searchLocation.longitude}
&key=${this.$appConfig.googleKey}`
        "
        :alt="`Map of ${searchLocation.name || ''}`"
        :class="{ 'grayscale': disabled }"
        style="width: 100%; border-radius: 10px;"
      />
      <div
        class="has-background-white p-4 is-rounded"
        :class="{
          'is-clickable': !disabled,
        }"
        style="border-radius: 10px;"
      >
        <p
          class="has-text-centered is-size-6 mx-auto"
          :class="{
            'has-text-grey-light': disabled,
            'has-text-info': !disabled,
          }"
        >
          View on a map
        </p>
      </div>
    </div>

    <!-- Buefy Modal -->
    <b-modal
      full-screen
      v-model="isMapModalActive"
      scroll="clip"
      :can-cancel="['escape']"
      @close="onModalClose"
    >
      <!-- Full-width container for the map -->
      <div class="modal-container">
        <!-- Top panel with back button, search location and close button -->
        <div class="top-panel">
          <div class="back-button-container">
            <button
              class="back-button"
              @click="closeMapModal"
              aria-label="Go back to search results"
            >
              <i class="fas fa-arrow-left"></i>
              <span>Back to Results</span>
            </button>
          </div>
          <div class="search-location-info">
            <h3 class="search-location-title">
              {{ searchLocation.name || "" }}
            </h3>
          </div>
          <button
            type="button"
            class="delete is-large is-primary close-button"
            @click="closeMapModal"
            aria-label="close"
          ></button>
        </div>

        <!-- Map Container - Full screen minus top panel -->
        <div id="hotel-map" ref="mapContainer" class="full-screen-map"></div>

        <!-- Hotel count info overlay -->
        <div class="map-info-overlay">
          <div class="info-card">
            <p class="is-size-7 has-text-white">
              Showing {{ hotelsInViewport }} of {{ hotelsCount }} hotels
            </p>
          </div>
        </div>

        <!-- Hotel Markers and Info Windows (rendered but not visible) -->
        <div v-if="map" style="display: none;">
          <hotel-map-marker
            v-for="hotel in hotels"
            :key="hotel.uniqueKey"
            ref="hotelMarkers"
            :hotel="hotel"
            :map="map"
            @marker-clicked="onMarkerClicked"
          />

          <hotel-map-info-window
            v-for="hotel in hotels"
            :key="`info-${hotel.uniqueKey}`"
            ref="hotelInfoWindows"
            :hotel="hotel"
            :map="map"
            :search-location-name="searchLocation.name || ''"
            @info-window-opened="onInfoWindowOpened"
          />
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import HotelMapMarker from "./HotelMapMarker.vue"
import HotelMapInfoWindow from "./HotelMapInfoWindow.vue"
import GoogleMapsService from "@/services/GoogleMapsService.js"
import { MarkerClusterer } from "@googlemaps/markerclusterer"

export default {
  name: "HotelMapModal",
  components: {
    HotelMapMarker,
    HotelMapInfoWindow,
  },
  props: {
    // future use for filters
    priceRange: {
      type: Object,
      default: () => ({ min: 0, max: 1000 }), // Default price range
      validator(value) {
        return (
          value &&
          typeof value.min === "number" &&
          typeof value.max === "number"
        )
      },
    },
    searchLocation: {
      type: Object,
      required: true,
      validator(value) {
        return (
          value &&
          typeof value.latitude === "number" &&
          typeof value.longitude === "number"
        )
      },
    },
    data: {
      type: Array,
      required: true,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isMapModalActive: false,
      map: null,
      markerClusterer: null,
      hotelsInViewport: 0,
      viewportUpdateTimeout: null,
    }
  },
  computed: {
    hotels() {
      return this.data
    },
    hotelsCount() {
      return this.data.length
    },
  },

  watch: {
    isMapModalActive(newValue, oldValue) {
      if (!newValue && oldValue) {
        // Modal is closing - ensure cleanup
        this.cleanup()
      }
    },
  },

  methods: {
    openMapModal() {
      if (this.disabled) return

      this.isMapModalActive = true

      // Push a new history state when opening modal
      if (window.history.pushState) {
        window.history.pushState(
          { modalOpen: true },
          "",
          window.location.href + "#map"
        )

        // Listen for popstate (back button)
        window.addEventListener("popstate", this.handleBackButton)
      }

      // Log the event
      this.$app.logEvent({
        event_type: 'Hotel Map Opened'
      });

      // Wait for modal to render before initializing map
      this.$nextTick(() => {
        // Add a small delay to ensure DOM is fully ready
        setTimeout(() => {
          this.initializeMap()
        }, 100)
      })
    },

    closeMapModal() {
      this.isMapModalActive = false
      this.cleanup()

      // Remove the history state if it was added by the modal
      if (window.history.state && window.history.state.modalOpen) {
        window.history.back()
      }

      // Remove event listener
      window.removeEventListener("popstate", this.handleBackButton)
    },

    onModalClose() {
      this.cleanup()

      // Remove event listener when modal is closed by other means (ESC, click outside)
      window.removeEventListener("popstate", this.handleBackButton)
    },

    handleBackButton(event) {
      if (this.isMapModalActive) {
        // Prevent the default back navigation
        event.preventDefault()

        // Close the modal instead
        this.isMapModalActive = false
        this.cleanup()

        // Remove the event listener
        window.removeEventListener("popstate", this.handleBackButton)
      }
    },

    async initializeMap() {
      try {
        // Load Google Maps API if not already loaded
        const google = await GoogleMapsService.getGoogleMaps()

        // Initialize map centered on search location
        const mapOptions = {
          //mapId: "DEMO_MAP_ID",
          center: {
            lat: this.searchLocation.latitude,
            lng: this.searchLocation.longitude,
          },
          zoom: 16,
          mapTypeId: "roadmap",
          styles: [
            {
              featureType: "poi.business",
              stylers: [{ visibility: "off" }],
            },
          ],
          mapTypeControl: false,
          mapTypeControlOptions: {
            style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
            position: google.maps.ControlPosition.TOP_CENTER,
          },
          zoomControl: true,
          zoomControlOptions: {
            position: google.maps.ControlPosition.RIGHT_TOP,
          },
          cameraControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        }

        this.map = new google.maps.Map(this.$refs.mapContainer, mapOptions)

        // Add search area indicator
        await this.addSearchAreaIndicator()

        // Set up map event listeners for viewport changes
        this.setupMapEventListeners()

        // Wait for child components to be ready, then setup clustering and bounds
        this.$nextTick(async () => {
          // Add delay to ensure all child components are mounted and markers created
          setTimeout(async () => {
            await this.ensureMarkersReady()
            this.setupMarkerClustering()
            await this.fitMapBounds()
            // Initial count of hotels in viewport
            this.calcHotelsInViewport()

            // Add debug method to window for testing
            //window.debugClustering = () => this.debugClustering()
          }, 200)
        })
      } catch (error) {
        console.error("Failed to initialize Google Maps:", error)
      }
    },

    async addSearchAreaIndicator() {
      const google = await GoogleMapsService.getGoogleMaps()

      // Add a circle to show the search area
      new google.maps.Circle({
        strokeColor: "#4285f4",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#4285f4",
        fillOpacity: 0.1,
        map: this.map,
        center: {
          lat: this.searchLocation.latitude,
          lng: this.searchLocation.longitude,
        },
        radius: this.searchLocation.radius ?? 3000, // radius in meters
      })

      // Add marker for search location
      new google.maps.Marker({
        position: {
          lat: this.searchLocation.latitude,
          lng: this.searchLocation.longitude,
        },
        map: this.map,
        title: "Search Location",
        icon: {
          url:
            "data:image/svg+xml;charset=UTF-8," +
            encodeURIComponent(`
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="8" fill="#4285f4" stroke="#fff" stroke-width="2"/>
              <circle cx="12" cy="12" r="3" fill="#fff"/>
            </svg>
          `),
          scaledSize: new google.maps.Size(24, 24),
          anchor: new google.maps.Point(12, 12),
        },
      })
    },

    async ensureMarkersReady() {
      // Wait for all marker components to be ready
      let attempts = 0
      const maxAttempts = 20 // 2 seconds max wait

      while (attempts < maxAttempts) {
        if (this.$refs.hotelMarkers && this.$refs.hotelMarkers.length > 0) {
          const readyMarkers = this.$refs.hotelMarkers
            .map((markerComponent) => markerComponent.getMarker())
            .filter((marker) => marker !== null)

          if (readyMarkers.length === this.$refs.hotelMarkers.length) {
            console.log(`All ${readyMarkers.length} markers are ready`)
            return
          }
        }

        // Wait 100ms before checking again
        await new Promise((resolve) => setTimeout(resolve, 100))
        attempts++
      }

      console.warn("Some markers may not be ready after waiting")
    },

    setupMarkerClustering() {
      if (!this.$refs.hotelMarkers) {
        console.log("No hotel markers ref found")
        return
      }

      console.log("setup marker clustering")
      const markers = this.$refs.hotelMarkers
        .map((markerComponent) => markerComponent.getMarker())
        .filter((marker) => marker !== null)

      console.log(
        `Found ${markers.length} valid markers out of ${this.$refs.hotelMarkers.length} components`
      )

      // Clear existing clusterer if any
      if (this.markerClusterer) {
        this.markerClusterer.clearMarkers()
        this.markerClusterer = null
      }

      // Initialize marker clustering if there are markers (lowered threshold for testing)
      if (markers.length > 0) {
        console.log(
          "initializing marker clusterer with",
          markers.length,
          "markers"
        )

        try {
          // Use the modern MarkerClusterer API
          this.markerClusterer = new MarkerClusterer({
            map: this.map,
            markers: markers,
            gridSize: 60,
            maxZoom: 15,
            minimumClusterSize: 2,
            renderer: {
              render: ({ count, position }) => {
                // Create custom cluster marker that matches the main marker style
                const clusterText =
                  count === 1 ? "1 property" : `${count} properties`

                // Calculate width based on text length
                const baseWidth = 80
                const extraWidth = Math.max(0, (clusterText.length - 12) * 4)
                const width = baseWidth + extraWidth
                const height = 40

                // Use the same red color as main markers
                const backgroundColor = "rgb(44 44 44 / 77%)"
                const textColor = "#ffffff"
                const fontSize = count < 100 ? "11" : "10"

                const svg = `
                  <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <!-- Cluster container background (above the pointer) -->
                    <rect x="2" y="2" width="${width -
                      4}" height="26" rx="13" fill="${backgroundColor}" stroke="${backgroundColor}" stroke-width="0"/>
                    <!-- Cluster text -->
                    <text x="${width /
                      2}" y="19" text-anchor="middle" fill="${textColor}" font-family="Arial, sans-serif" font-size="${fontSize}" font-weight="bold">${clusterText}</text>
                    <!-- Pointer/tail pointing downward from bottom of container -->
                    <path d="M${width / 2 - 5} 28 L${width / 2 +
                  5} 28 L${width /
                  2} 38 Z" fill="${backgroundColor}" stroke="${backgroundColor}" stroke-width="0"/>
                  </svg>
                `

                return new window.google.maps.Marker({
                  position,
                  icon: {
                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                      svg
                    )}`,
                    scaledSize: new window.google.maps.Size(width, height),
                    anchor: new window.google.maps.Point(width / 2, height - 2),
                  },
                  label: {
                    text: "",
                    color: "transparent",
                  },
                })
              },
            },
          })

          console.log("MarkerClusterer initialized successfully")
        } catch (error) {
          console.error("Error initializing MarkerClusterer:", error)

          // Fallback: try the old API syntax
          try {
            console.log("Trying fallback MarkerClusterer API")
            this.markerClusterer = new MarkerClusterer(this.map, markers, {
              gridSize: 60,
              maxZoom: 15,
              minimumClusterSize: 2,
            })
            console.log("Fallback MarkerClusterer initialized successfully")
          } catch (fallbackError) {
            console.error(
              "Fallback MarkerClusterer also failed:",
              fallbackError
            )
          }
        }
      } else {
        console.log("Not enough markers for clustering")
      }
    },

    onMarkerClicked(marker, hotel) {
      // Find the corresponding info window component
      const infoWindowIndex = this.hotels.findIndex(
        (h) => h.uniqueKey === hotel.uniqueKey
      )
      if (
        infoWindowIndex !== -1 &&
        this.$refs.hotelInfoWindows[infoWindowIndex]
      ) {
        this.$refs.hotelInfoWindows[infoWindowIndex].openInfoWindow(marker)
      }
    },

    onInfoWindowOpened(openedInfoWindow) {
      // Close all other info windows
      if (this.$refs.hotelInfoWindows) {
        this.$refs.hotelInfoWindows.forEach((infoWindowComponent) => {
          const infoWindow = infoWindowComponent.getInfoWindow()
          if (infoWindow && infoWindow !== openedInfoWindow) {
            infoWindow.close()
          }
        })
      }
    },

    async fitMapBounds() {
      if (!this.$refs.hotelMarkers || this.$refs.hotelMarkers.length === 0)
        return

      const google = await GoogleMapsService.getGoogleMaps()
      const bounds = new google.maps.LatLngBounds()

      // Include search location
      bounds.extend({
        lat: this.searchLocation.latitude,
        lng: this.searchLocation.longitude,
      })

      // Include all hotel markers
      this.$refs.hotelMarkers.forEach((markerComponent) => {
        const position = markerComponent.getPosition()
        if (position) {
          bounds.extend(position)
        }
      })

      this.map.fitBounds(bounds, 0)

      // Ensure minimum zoom level
      //google.maps.event.addListenerOnce(this.map, "bounds_changed", () => {
      //  if (this.map.getZoom() > 18) {
      //    this.map.setZoom(18)
      //  }
      //})
    },

    setupMapEventListeners() {
      if (!this.map) return

      // Listen for map bounds changes (zoom, pan, etc.)
      this.map.addListener("bounds_changed", () => {
        // Debounce the update to avoid too many calls
        //clearTimeout(this.viewportUpdateTimeout);
        //this.viewportUpdateTimeout = setTimeout(() => {
        //  this.calcHotelsInViewport();
        //}, 300);
      })

      // Listen for idle event (when map stops moving/zooming)
      this.map.addListener("idle", () => {
        this.calcHotelsInViewport()
      })
    },

    calcHotelsInViewport() {
      if (!this.map || !this.$refs.hotelMarkers) {
        this.hotelsInViewport = 0
        return
      }

      const bounds = this.map.getBounds()
      if (!bounds) {
        this.hotelsInViewport = 0
        return
      }

      let count = 0
      this.$refs.hotelMarkers.forEach((markerComponent) => {
        const position = markerComponent.getPosition()
        if (position && bounds.contains(position)) {
          count++
        }
      })

      this.hotelsInViewport = count
    },

    // Debug method to manually test clustering
    debugClustering() {
      console.log("=== Clustering Debug Info ===")
      console.log("Map:", this.map)
      console.log("Hotel markers refs:", this.$refs.hotelMarkers)

      if (this.$refs.hotelMarkers) {
        const markers = this.$refs.hotelMarkers
          .map((markerComponent, index) => {
            const marker = markerComponent.getMarker()
            console.log(
              `Marker ${index}:`,
              marker,
              markerComponent.hotel?.hotelName
            )
            return marker
          })
          .filter((marker) => marker !== null)

        console.log("Valid markers:", markers.length)
        console.log("MarkerClusterer instance:", this.markerClusterer)

        // Force re-setup clustering
        this.setupMarkerClustering()
      }
    },

    cleanup() {
      // Clear timeout
      if (this.viewportUpdateTimeout) {
        clearTimeout(this.viewportUpdateTimeout)
        this.viewportUpdateTimeout = null
      }

      // Close all info windows
      if (this.$refs.hotelInfoWindows) {
        this.$refs.hotelInfoWindows.forEach((infoWindowComponent) => {
          infoWindowComponent.closeInfoWindow()
        })
      }

      // Clear clusterer
      if (this.markerClusterer) {
        try {
          this.markerClusterer.clearMarkers()
          this.markerClusterer.setMap(null)
        } catch (error) {
          console.warn("Error clearing marker clusterer:", error)
        }
        this.markerClusterer = null
      }

      // Reset viewport count
      this.hotelsInViewport = 0

      // Clear map reference
      this.map = null
    },
  },

  beforeDestroy() {
    this.cleanup()
    // Ensure popstate listener is removed
    window.removeEventListener("popstate", this.handleBackButton)
  },
}
</script>

<style scoped>
/* Modal container */
.modal-container {
  position: relative;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Top panel - white background with black text */
.top-panel {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #ffffff;
  color: #333333;
  padding: 0.8rem 1.5rem;
  min-height: 50px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid #e0e0e0;
  z-index: 1000;
  flex-shrink: 0;
}

/* Back button */
.back-button-container {
  flex-shrink: 0;
  margin-right: 16px;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 8px 12px;
  color: #333;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
}

.back-button:hover {
  background: #f5f5f5;
  border-color: #ccc;
  color: #000;
}

.back-button:focus {
  outline: 2px solid #2c5aa0;
  outline-offset: 2px;
}

.back-button i {
  font-size: 12px;
}

/* Search location info */
.search-location-info {
  flex: 1;
}

.search-location-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
  color: #333333;
}

/* Close button - styled for contrast against white background */
.close-button {
  background-color: #f8f9fa;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.close-button:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
  transform: scale(1.05);
}

.close-button:before,
.close-button:after {
  background-color: #212529 !important;
}

/* Full-screen map */
.full-screen-map {
  width: 100%;
  flex: 1;
  border: none;
  border-radius: 0;
}

/* Info overlay positioned in bottom-left corner */
.map-info-overlay {
  position: absolute;
  bottom: 20px;
  left: 20px;
  z-index: 1000;
  pointer-events: none;
}

.info-card {
  background-color: rgba(0, 0, 0, 0.6);
  padding: 12px 16px;
  border-radius: 8px;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

/* Responsive adjustments */
@media screen and (max-width: 600px) {
  /* Back button responsive adjustments */
  .back-button-container {
    margin-right: 8px;
  }

  .back-button {
    padding: 6px 8px;
    font-size: 0.8rem;
  }

  .back-button span {
    display: none; /* Hide text on small screens, keep icon */
  }

  .back-button i {
    font-size: 14px;
  }

  .search-location-title {
    font-size: 1.125rem;
  }

  .search-location-subtitle {
    font-size: 0.8rem;
  }
}

@media screen and (max-width: 450px) {
  .back-button {
    padding: 4px 6px;
  }

  .back-button i {
    font-size: 16px;
  }

  .search-location-title {
    font-size: 1rem;
  }
}

/* Override Buefy modal styles for full-screen experience */
:deep(.modal-card) {
  margin: 0 !important;
  max-height: none !important;
  height: 100vh !important;
  width: 100vw !important;
  border-radius: 0 !important;
  overflow: hidden !important;
}

:deep(.modal-background) {
  background-color: rgba(0, 0, 0, 0.8);
}

:deep(.modal) {
  overflow: hidden !important;
}

/* Disabled state styles */
.grayscale {
  filter: grayscale(100%);
  transition: filter 0.2s ease;
}
</style>
